import {Card, Col, Row, Typography, Select, DatePicker, Space, Statistic, Progress, InputNumber, Button, Checkbox, Tag} from "antd";
import {useDispatch, useSelector} from "react-redux";
import {useEffect, useState} from "react";
import {loadLetters, loadAllStacks} from "../store/app/app.slice";
import {MailOutlined, LineChartOutlined, HistoryOutlined, CheckCircleOutlined, GlobalOutlined, UserOutlined, TagOutlined, EditOutlined, SaveOutlined, TranslationOutlined} from "@ant-design/icons";
import moment from "moment";
import {getLanguagesLabel, getTopicsLabel} from "../components/helper/labelHelper";

const {RangePicker} = DatePicker;
const {Title} = Typography;

export default function Dashboard() {
    const dispatch = useDispatch();
    const {letters, allStacks} = useSelector((state) => state.app);
    const [dateRange, setDateRange] = useState([moment().subtract(30, 'days'), moment()]);
    const [selectedStatus, setSelectedStatus] = useState([]);
    const [isEditingAgeRanges, setIsEditingAgeRanges] = useState(false);
    const [isEditingLanguageRanges, setIsEditingLanguageRanges] = useState(false);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedLanguages, setSelectedLanguages] = useState([]);
    const [selectedCities, setSelectedCities] = useState([]);
    const [selectedCountries, setSelectedCountries] = useState([]);
    const [ageRanges, setAgeRanges] = useState({
        range1: {min: 0, max: 18},
        range2: {min: 19, max: 30},
        range3: {min: 31, max: 50},
        range4: {min: 51, max: 999}
    });
    const [languageRanges, setLanguageRanges] = useState({
        range1: {min: 0, max: 25},
        range2: {min: 26, max: 50},
        range3: {min: 51, max: 75},
        range4: {min: 76, max: 100}
    });
    const [minCityLetters, setMinCityLetters] = useState(20);
    const [selectedCardCategories, setSelectedCardCategories] = useState([]);
    const [selectedCardLanguages, setSelectedCardLanguages] = useState([]);

    useEffect(() => {
        dispatch(loadLetters({}));
        dispatch(loadAllStacks({}));
    }, []);

    const getLettersByStatus = (status) => {
        return letters.filter(letter => letter.internalStatus === status);
    };

    const getLettersInDateRange = (letters) => {
        return letters.filter(letter => {
            const letterDate = moment(letter.created);
            return letterDate.isBetween(dateRange[0], dateRange[1], 'day', '[]');
        });
    };

    const getStacksByStatus = (status) => {
        return allStacks.filter(stack => stack.status === status);
    };

    const getUniqueValues = (field) => {
        const values = new Set();
        letters.forEach(letter => {
            if (field === 'category') {
                const value = letter.originalLetter?.category;
                if (value) values.add(getTopicsLabel(value));
            } else if (field === 'language') {
                const value = letter.originalLetter?.language;
                if (value) values.add(getLanguagesLabel(value));
            } else if (field === 'city') {
                const value = letter.originalLetter?.sender?.city;
                if (value) values.add(value);
            } else if (field === 'country') {
                const value = letter.originalLetter?.sender?.country?.defaultLabel;
                if (value) values.add(value);
            }
        });
        return Array.from(values).sort();
    };

    const handleFilterChange = (type, value) => {
        switch(type) {
            case 'status':
                setSelectedStatus(value);
                break;
            case 'category':
                setSelectedCategories(value);
                break;
            case 'language':
                setSelectedLanguages(value);
                break;
            case 'city':
                setSelectedCities(value);
                break;
            case 'country':
                setSelectedCountries(value);
                break;
        }
    };

    const filteredLetters = letters.filter(letter => {
        if (!letter.originalLetter) return false;
        
        // If any filter is empty, show all data
        if (selectedStatus.length === 0 && 
            selectedCategories.length === 0 && 
            selectedLanguages.length === 0 && 
            selectedCities.length === 0 && 
            selectedCountries.length === 0) {
            return true;
        }
        
        const matchesStatus = selectedStatus.length === 0 || selectedStatus.includes(letter.internalStatus);
        const matchesCategory = selectedCategories.length === 0 || selectedCategories.includes(getTopicsLabel(letter.originalLetter.category));
        const matchesLanguage = selectedLanguages.length === 0 || selectedLanguages.includes(getLanguagesLabel(letter.originalLetter.language));
        const matchesCity = selectedCities.length === 0 || selectedCities.includes(letter.originalLetter.sender?.city);
        const matchesCountry = selectedCountries.length === 0 || selectedCountries.includes(letter.originalLetter.sender?.country?.defaultLabel);
        
        return matchesStatus && matchesCategory && matchesLanguage && matchesCity && matchesCountry;
    });

    const lettersInRange = getLettersInDateRange(filteredLetters);

    // Calculate age distribution
    const calculateAge = (birthdate) => {
        if (!birthdate) return null;
        return moment().diff(moment(birthdate), 'years');
    };

    const getAgeDistribution = () => {
        const ageGroups = {
            [`${ageRanges.range1.min}-${ageRanges.range1.max}`]: 0,
            [`${ageRanges.range2.min}-${ageRanges.range2.max}`]: 0,
            [`${ageRanges.range3.min}-${ageRanges.range3.max}`]: 0,
            [`${ageRanges.range4.min}+`]: 0
        };

        lettersInRange.forEach(letter => {
            const age = calculateAge(letter.originalLetter?.sender?.birthdate);
            if (age !== null) {
                if (age <= ageRanges.range1.max) ageGroups[`${ageRanges.range1.min}-${ageRanges.range1.max}`]++;
                else if (age <= ageRanges.range2.max) ageGroups[`${ageRanges.range2.min}-${ageRanges.range2.max}`]++;
                else if (age <= ageRanges.range3.max) ageGroups[`${ageRanges.range3.min}-${ageRanges.range3.max}`]++;
                else ageGroups[`${ageRanges.range4.min}+`]++;
            }
        });

        return ageGroups;
    };

    const handleAgeRangeChange = (range, field, value) => {
        setAgeRanges(prev => ({
            ...prev,
            [range]: {
                ...prev[range],
                [field]: value
            }
        }));
    };

    const resetAgeRanges = () => {
        setAgeRanges({
            range1: {min: 0, max: 18},
            range2: {min: 19, max: 30},
            range3: {min: 31, max: 50},
            range4: {min: 51, max: 999}
        });
    };

    // Calculate geographical distribution
    const getGeographicalDistribution = () => {
        const countries = {};
        const cities = {};
        lettersInRange.forEach(letter => {
            const country = letter.originalLetter?.sender?.country?.defaultLabel;
            const city = letter.originalLetter?.sender?.city;
            if (country) {
                countries[country] = (countries[country] || 0) + 1;
            }
            if (city) {
                cities[city] = (cities[city] || 0) + 1;
            }
        });
        return {
            countries: Object.entries(countries)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 5),
            cities: Object.entries(cities)
                .sort((a, b) => b[1] - a[1])
        };
    };

    const geographicalDistribution = getGeographicalDistribution();
    const filteredCities = geographicalDistribution.cities.filter(([_, count]) => count >= minCityLetters);

    // Calculate category distribution
    const getCategoryDistribution = () => {
        const categories = {};
        lettersInRange.forEach(letter => {
            const category = letter.originalLetter?.category;
            if (category) {
                const label = getTopicsLabel(category);
                categories[label] = (categories[label] || 0) + 1;
            }
        });
        return Object.entries(categories)
            .sort((a, b) => b[1] - a[1]);
    };

    const ageDistribution = getAgeDistribution();
    const categoryDistribution = getCategoryDistribution();
    const allCategories = categoryDistribution.map(([category]) => category);

    const filteredCategoryDistribution = categoryDistribution.filter(([category]) => 
        selectedCategories.includes(category)
    );

    const getSummaryDistribution = () => {
        const summaryStats = {
            'With Summary': 0,
            'Without Summary': 0,
            'Presented to Huzoor': 0,
            'Not Presented to Huzoor': 0
        };

        lettersInRange.forEach(letter => {
            if (letter.summary) {
                summaryStats['With Summary']++;
            } else {
                summaryStats['Without Summary']++;
            }
            if (letter.presentToHudhur) {
                summaryStats['Presented to Huzoor']++;
            } else {
                summaryStats['Not Presented to Huzoor']++;
            }
        });

        return Object.entries(summaryStats);
    };

    const summaryDistribution = getSummaryDistribution();

    const stats = [
        {
            title: "Total Letters",
            value: lettersInRange.length,
            icon: <MailOutlined style={{fontSize: '24px', color: '#1890ff'}}/>,
            color: "#1890ff"
        },
        {
            title: "Pending Summaries",
            value: getLettersByStatus("LETTER_READY_FOR_SUMMARY").length,
            icon: <LineChartOutlined style={{fontSize: '24px', color: '#e89d69'}}/>,
            color: "#e89d69"
        },
        {
            title: "Pending Filterations",
            value: getLettersByStatus("LETTER_IN_FILTRATION").length,
            icon: <HistoryOutlined style={{fontSize: '24px', color: '#5c5959'}}/>,
            color: "#5c5959"
        },
        {
            title: "Processed Letters",
            value: getLettersByStatus("LETTER_TOTALLY_PROCESSED").length,
            icon: <CheckCircleOutlined style={{fontSize: '24px', color: '#186700'}}/>,
            color: "#186700"
        }
    ];

    const stackStats = [
        {
            title: "Requested Stacks",
            value: getStacksByStatus("REQUESTED").length,
            color: "#1890ff"
        },
        {
            title: "Assigned Stacks",
            value: getStacksByStatus("ASSIGNED").length,
            color: "#e89d69"
        },
        {
            title: "Delivered Stacks",
            value: getStacksByStatus("DELIVERED").length,
            color: "#5c5959"
        },
        {
            title: "Closed Stacks",
            value: getStacksByStatus("CLOSED").length,
            color: "#186700"
        }
    ];

    const getLanguageDistribution = () => {
        const languages = {};
        lettersInRange.forEach(letter => {
            const language = letter.originalLetter?.language;
            if (language) {
                const label = getLanguagesLabel(language);
                languages[label] = (languages[label] || 0) + 1;
            }
        });
        return Object.entries(languages)
            .sort((a, b) => b[1] - a[1]);
    };

    const languageDistribution = getLanguageDistribution();
    const allLanguages = languageDistribution.map(([language]) => language);

    const filteredLanguageDistribution = languageDistribution.filter(([language]) => 
        selectedLanguages.includes(language)
    );

    return (
        <div className="layout-content">
            <Row gutter={[24, 24]} className="mb-24">
                <Col span={24}>
                    <Card>
                        <Space direction="vertical" size="large" style={{width: '100%'}}>
                            <Space wrap>
                                <RangePicker 
                                    value={dateRange}
                                    onChange={setDateRange}
                                />
                                <Select
                                    mode="multiple"
                                    placeholder="Filter by status"
                                    style={{width: 300}}
                                    value={selectedStatus}
                                    onChange={(value) => handleFilterChange('status', value)}
                                    options={[
                                        {label: "Received", value: "LETTER_RECEIVED"},
                                        {label: "In Filtration", value: "LETTER_IN_FILTRATION"},
                                        {label: "Filtered", value: "LETTER_FILTERED"},
                                        {label: "Ready for Summary", value: "LETTER_READY_FOR_SUMMARY"},
                                        {label: "In Summary", value: "LETTER_IN_SUMMARY"},
                                        {label: "Summarized", value: "LETTER_SUMMARIZED"},
                                        {label: "Ready for Answer", value: "LETTER_READY_FOR_ANSWER"},
                                        {label: "In Answer", value: "LETTER_IN_ANSWER"},
                                        {label: "Answered", value: "LETTER_ANSWERED"},
                                        {label: "Totally Processed", value: "LETTER_TOTALLY_PROCESSED"}
                                    ]}
                                />
                                <Select
                                    mode="multiple"
                                    placeholder="Filter by topic"
                                    style={{width: 200}}
                                    value={selectedCategories}
                                    onChange={(value) => handleFilterChange('category', value)}
                                    options={getUniqueValues('category').map(category => ({
                                        label: category,
                                        value: category
                                    }))}
                                />
                                <Select
                                    mode="multiple"
                                    placeholder="Filter by language"
                                    style={{width: 200}}
                                    value={selectedLanguages}
                                    onChange={(value) => handleFilterChange('language', value)}
                                    options={getUniqueValues('language').map(language => ({
                                        label: language,
                                        value: language
                                    }))}
                                />
                                <Select
                                    mode="multiple"
                                    placeholder="Filter by city"
                                    style={{width: 200}}
                                    value={selectedCities}
                                    onChange={(value) => handleFilterChange('city', value)}
                                    options={getUniqueValues('city').map(city => ({
                                        label: city,
                                        value: city
                                    }))}
                                />
                                <Select
                                    mode="multiple"
                                    placeholder="Filter by country"
                                    style={{width: 200}}
                                    value={selectedCountries}
                                    onChange={(value) => handleFilterChange('country', value)}
                                    options={getUniqueValues('country').map(country => ({
                                        label: country,
                                        value: country
                                    }))}
                                />
                            </Space>
                        </Space>
                    </Card>
                </Col>
            </Row>

            <Row gutter={[24, 24]} className="rowgap-vbox mb-24">
                {stats.map((stat, index) => (
                    <Col key={index} xs={24} sm={24} md={12} lg={6} xl={6}>
                        <Card bordered={false} className="criclebox">
                            <div className="number">
                                <Row align="middle" gutter={[24, 0]}>
                                    <Col xs={18}>
                                        <span>{stat.title}</span>
                                        <Title level={3}>
                                            {stat.value}
                                        </Title>
                                    </Col>
                                    <Col xs={6}>
                                        <div className="icon-box">{stat.icon}</div>
                                    </Col>
                                </Row>
                            </div>
                        </Card>
                    </Col>
                ))}
            </Row>

            <Row gutter={[24, 24]} className="mb-24">
                <Col span={24}>
                    <Card title="Stack Statistics" bordered={false} className="criclebox">
                        <Row gutter={[24, 24]}>
                            {stackStats.map((stat, index) => (
                                <Col key={index} xs={24} sm={12} md={6}>
                                    <Statistic
                                        title={stat.title}
                                        value={stat.value}
                                        valueStyle={{color: stat.color}}
                                    />
                                </Col>
                            ))}
                        </Row>
                    </Card>
                </Col>
            </Row>

            <Row gutter={[24, 24]} className="mb-24">
                <Col xs={24} md={12}>
                    <Card 
                        title={
                            <Space>
                                <UserOutlined /> Age Distribution
                                <Button 
                                    type="primary"
                                    icon={isEditingAgeRanges ? <SaveOutlined /> : <EditOutlined />}
                                    onClick={() => {
                                        if (isEditingAgeRanges) {
                                            setIsEditingAgeRanges(false);
                                        } else {
                                            setIsEditingAgeRanges(true);
                                        }
                                    }}
                                >
                                    {isEditingAgeRanges ? 'Save' : 'Edit'}
                                </Button>
                            </Space>
                        } 
                        bordered={false} 
                        className="h-full"
                    >
                        {isEditingAgeRanges ? (
                            <Space direction="vertical" style={{width: '100%'}}>
                                {Object.entries(ageRanges).map(([range, values], index) => (
                                    <Card size="small" key={range} style={{marginBottom: 8}}>
                                        <Space align="center">
                                            <span>Range {index + 1}:</span>
                                            <InputNumber
                                                min={0}
                                                max={values.max}
                                                value={values.min}
                                                onChange={(value) => handleAgeRangeChange(range, 'min', value)}
                                                style={{width: 80}}
                                            />
                                            <span>-</span>
                                            <InputNumber
                                                min={values.min}
                                                max={999}
                                                value={values.max}
                                                onChange={(value) => handleAgeRangeChange(range, 'max', value)}
                                                style={{width: 80}}
                                            />
                                            {index === 3 && <span>+</span>}
                                        </Space>
                                    </Card>
                                ))}
                                <Button type="primary" onClick={resetAgeRanges} block>Reset to Default</Button>
                            </Space>
                        ) : (
                            Object.entries(ageDistribution).map(([ageGroup, count]) => (
                                <div key={ageGroup} style={{marginBottom: 16}}>
                                    <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: 8}}>
                                        <span>{ageGroup}</span>
                                        <span>{count} letters</span>
                                    </div>
                                    <Progress 
                                        percent={Math.round((count / lettersInRange.length) * 100)} 
                                        showInfo={false}
                                        strokeColor={{
                                            [`${ageRanges.range1.min}-${ageRanges.range1.max}`]: '#1890ff',
                                            [`${ageRanges.range2.min}-${ageRanges.range2.max}`]: '#52c41a',
                                            [`${ageRanges.range3.min}-${ageRanges.range3.max}`]: '#faad14',
                                            [`${ageRanges.range4.min}+`]: '#f5222d'
                                        }[ageGroup]}
                                    />
                                </div>
                            ))
                        )}
                    </Card>
                </Col>

                <Col xs={24} md={12}>
                    <Card title={<><GlobalOutlined /> Top Countries</>} bordered={false} className="h-full">
                        {geographicalDistribution.countries.map(([country, count]) => (
                            <div key={country} style={{marginBottom: 16}}>
                                <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: 8}}>
                                    <span>{country}</span>
                                    <span>{count} letters</span>
                                </div>
                                <Progress 
                                    percent={Math.round((count / lettersInRange.length) * 100)} 
                                    showInfo={false}
                                    strokeColor="#722ed1"
                                />
                            </div>
                        ))}
                    </Card>
                </Col>
            </Row>

            <Row gutter={[24, 24]} className="mb-24">
                <Col xs={24} md={12}>
                    <Card 
                        title={
                            <Space>
                                <GlobalOutlined /> Cities
                                <InputNumber
                                    min={1}
                                    value={minCityLetters}
                                    onChange={setMinCityLetters}
                                    addonBefore="Min letters"
                                    style={{ width: 150 }}
                                />
                            </Space>
                        } 
                        bordered={false} 
                        className="h-full"
                    >
                        {filteredCities.map(([city, count]) => (
                            <div key={city} style={{marginBottom: 16}}>
                                <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: 8}}>
                                    <span>{city}</span>
                                    <span>{count} letters</span>
                                </div>
                                <Progress 
                                    percent={Math.round((count / lettersInRange.length) * 100)} 
                                    showInfo={false}
                                    strokeColor="#13c2c2"
                                />
                            </div>
                        ))}
                    </Card>
                </Col>
            </Row>

            <Row gutter={[24, 24]} className="mb-24">
                <Col span={24}>
                    <Card 
                        title={
                            <Space>
                                <TagOutlined /> Letter Categories
                                <Select
                                    mode="multiple"
                                    style={{ width: '100%', minWidth: 300 }}
                                    placeholder="Select categories"
                                    value={selectedCardCategories}
                                    onChange={setSelectedCardCategories}
                                    options={allCategories.map(category => ({
                                        label: category,
                                        value: category
                                    }))}
                                />
                            </Space>
                        } 
                        bordered={false}
                    >
                        <div style={{ padding: '20px 0' }}>
                            {categoryDistribution
                                .filter(([category]) => selectedCardCategories.length === 0 || selectedCardCategories.includes(category))
                                .map(([category, count]) => (
                                    <div key={category} style={{ marginBottom: 16 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                            <span>{category}</span>
                                            <span>{count} letters ({Math.round((count / lettersInRange.length) * 100)}%)</span>
                                        </div>
                                        <Progress 
                                            percent={Math.round((count / lettersInRange.length) * 100)} 
                                            showInfo={false}
                                            strokeColor="#1890ff"
                                            size="large"
                                        />
                                    </div>
                                ))}
                        </div>
                    </Card>
                </Col>
            </Row>

            <Row gutter={[24, 24]} className="mb-24">
                <Col xs={24} md={12}>
                    <Card 
                        title={
                            <Space>
                                <TranslationOutlined /> Language Statistics
                                <Select
                                    mode="multiple"
                                    style={{ width: '100%', minWidth: 300 }}
                                    placeholder="Select languages"
                                    value={selectedCardLanguages}
                                    onChange={setSelectedCardLanguages}
                                    options={allLanguages.map(language => ({
                                        label: language,
                                        value: language
                                    }))}
                                />
                            </Space>
                        } 
                        bordered={false} 
                        className="h-full"
                    >
                        <div style={{ padding: '20px 0' }}>
                            {languageDistribution
                                .filter(([language]) => selectedCardLanguages.length === 0 || selectedCardLanguages.includes(language))
                                .map(([language, count]) => (
                                    <div key={language} style={{ marginBottom: 16 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                            <span>{language}</span>
                                            <span>{count} letters ({Math.round((count / lettersInRange.length) * 100)}%)</span>
                                        </div>
                                        <Progress 
                                            percent={Math.round((count / lettersInRange.length) * 100)} 
                                            showInfo={false}
                                            strokeColor="#1890ff"
                                            size="large"
                                        />
                                    </div>
                                ))}
                        </div>
                    </Card>
                </Col>
            </Row>

            <Row gutter={[24, 24]} className="mb-24">
                <Col xs={24} md={12}>
                    <Card 
                        title={<Space><TagOutlined /> Summary Statistics</Space>}
                        bordered={false}
                    >
                        <div style={{ padding: '20px 0' }}>
                            {summaryDistribution.map(([label, count]) => (
                                <div key={label} style={{ marginBottom: 16 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                        <span>{label}</span>
                                        <span>{count} letters ({Math.round((count / lettersInRange.length) * 100)}%)</span>
                                    </div>
                                    <Progress 
                                        percent={Math.round((count / lettersInRange.length) * 100)} 
                                        showInfo={false}
                                        strokeColor={label.includes('With') || label.includes('Presented') ? '#52c41a' : '#ff4d4f'}
                                        size="large"
                                    />
                                </div>
                            ))}
                        </div>
                    </Card>
                </Col>
            </Row>
        </div>
    );
}